import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type {
  RoomSectionCameraDir,
  RoomSectionCenterCoordinate,
  RoomSectionDirection,
  RoomSectionRenderItem,
} from "./types";

export type ViewMode = "fixed" | "orbit" | "walk";

const disposeSceneResources = (scene: THREE.Scene) => {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();

  scene.traverse((object) => {
    const maybeGeometry = (object as { geometry?: THREE.BufferGeometry })
      .geometry;
    if (maybeGeometry) {
      geometries.add(maybeGeometry);
    }

    const maybeMaterial = (
      object as { material?: THREE.Material | THREE.Material[] }
    ).material;
    if (Array.isArray(maybeMaterial)) {
      maybeMaterial.forEach((material) => materials.add(material));
    } else if (maybeMaterial) {
      materials.add(maybeMaterial);
    }
  });

  geometries.forEach((geometry) => geometry.dispose());
  materials.forEach((material) => material.dispose());
};

export type RoomSectionIsoViewerProps = {
  cameraDir?: RoomSectionCameraDir;
  renderItems: RoomSectionRenderItem[];
  sectionCenterCoordinate: RoomSectionCenterCoordinate | null;
  selectedSection: RoomSectionDirection;
  onItemClick?: (item: RoomSectionRenderItem["item"]) => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
};

export const RoomSectionIsoViewer: React.FC<RoomSectionIsoViewerProps> = ({
  cameraDir = { x: 1, y: 1, z: 1 },
  renderItems,
  sectionCenterCoordinate,
  selectedSection,
  onItemClick,
  viewMode: viewModeProp,
  onViewModeChange,
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const onItemClickRef = useRef(onItemClick);
  useEffect(() => { onItemClickRef.current = onItemClick; }, [onItemClick]);
  const orbitCameraStateRef = useRef<{ position: THREE.Vector3; target: THREE.Vector3 } | null>(null);
  const walkCameraStateRef = useRef<{ position: THREE.Vector3; lookDir: THREE.Vector3 } | null>(null);
  const [viewModeInternal, setViewModeInternal] = useState<ViewMode>("orbit");
  const viewMode = viewModeProp ?? viewModeInternal;
  const setViewMode = onViewModeChange ?? setViewModeInternal;

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const w = container.clientWidth || 400;
    const h = container.clientHeight || 300;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8f9fa);

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.0);
    dirLight.position.set(1, 1, 3);
    scene.add(dirLight);

    const renderObbs = renderItems.flatMap(
      ({ item, color, opacity, outline }) =>
        (item.geometry?.Obb3DWorldMmList ?? []).map((obb) => ({
          item,
          obb,
          color,
          opacity,
          outline,
        })),
    );
    const obbs = renderObbs.map(({ obb }) => obb);
    if (obbs.length === 0) return;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;

    for (const obb of obbs) {
      const { x, y, z } = obb.centerMm;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      minZ = Math.min(minZ, z);
      maxZ = Math.max(maxZ, z);
    }

    const center = new THREE.Vector3(
      (minX + maxX) / 2,
      (minY + maxY) / 2,
      (minZ + maxZ) / 2,
    );
    const sceneSize = Math.max(maxX - minX, maxY - minY, maxZ - minZ, 1);

    const camDirVec = new THREE.Vector3(
      cameraDir.x,
      cameraDir.y,
      cameraDir.z,
    ).normalize();
    const useClipping =
      selectedSection !== "isometric" && sectionCenterCoordinate != null;

    let clipPlane: THREE.Plane | null = null;
    if (useClipping && sectionCenterCoordinate) {
      const [wx, wy] = sectionCenterCoordinate;
      const widgetCenter = new THREE.Vector3(wx, wy, 0);
      const planeNormal = camDirVec.clone().negate();
      clipPlane = new THREE.Plane(planeNormal, camDirVec.dot(widgetCenter));
    }

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      stencil: useClipping,
    });
    renderer.setSize(w, h);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.localClippingEnabled = useClipping;
    container.appendChild(renderer.domElement);

    const visualMeshes: THREE.Mesh[] = [];

    for (const { item, obb, color, opacity, outline } of renderObbs) {
      const {
        centerMm: c,
        axisA,
        axisB,
        axisC,
        extentAMm: eA,
        extentBMm: eB,
        extentCMm: eC,
      } = obb;

      const geometry = new THREE.BoxGeometry(
        (2 * eA) / 1000,
        (2 * eB) / 1000,
        (2 * eC) / 1000,
      );

      const material = new THREE.MeshPhongMaterial({
        color: new THREE.Color(color),
        specular: new THREE.Color(0x224466),
        shininess: 40,
        opacity,
        transparent: opacity < 1,
        clippingPlanes: clipPlane ? [clipPlane] : [],
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.userData.item = item;
      mesh.position.set(c.x, c.y, c.z);
      mesh.quaternion.setFromRotationMatrix(
        new THREE.Matrix4().set(
          axisA.x,
          axisB.x,
          axisC.x,
          0,
          axisA.y,
          axisB.y,
          axisC.y,
          0,
          axisA.z,
          axisB.z,
          axisC.z,
          0,
          0,
          0,
          0,
          1,
        ),
      );
      mesh.renderOrder = 2;
      scene.add(mesh);
      visualMeshes.push(mesh);

      if (outline) {
        const edges = new THREE.EdgesGeometry(geometry);
        const lineMaterial = new THREE.LineBasicMaterial({
          color: new THREE.Color(outline.color),
          opacity: outline.opacity ?? 1,
          transparent: (outline.opacity ?? 1) < 1,
          linewidth: outline.width ?? 1,
          depthTest: outline.renderOnTop === false,
          depthWrite: outline.renderOnTop === false,
          clippingPlanes: clipPlane ? [clipPlane] : [],
        });
        const lineSegments = new THREE.LineSegments(edges, lineMaterial);
        lineSegments.position.copy(mesh.position);
        lineSegments.quaternion.copy(mesh.quaternion);
        lineSegments.renderOrder = outline.renderOnTop === false ? 2 : 5;
        scene.add(lineSegments);
      }
    }

    if (clipPlane && useClipping) {
      for (const visMesh of visualMeshes) {
        const backMat = new THREE.MeshBasicMaterial({
          side: THREE.BackSide,
          depthWrite: false,
          depthTest: false,
          colorWrite: false,
          stencilWrite: true,
          stencilFunc: THREE.AlwaysStencilFunc,
          stencilFail: THREE.IncrementWrapStencilOp,
          stencilZFail: THREE.IncrementWrapStencilOp,
          stencilZPass: THREE.IncrementWrapStencilOp,
        });
        const backMesh = new THREE.Mesh(visMesh.geometry, backMat);
        backMesh.position.copy(visMesh.position);
        backMesh.quaternion.copy(visMesh.quaternion);
        backMesh.renderOrder = 1;
        scene.add(backMesh);

        const frontMat = new THREE.MeshBasicMaterial({
          side: THREE.FrontSide,
          depthWrite: false,
          depthTest: false,
          colorWrite: false,
          stencilWrite: true,
          stencilFunc: THREE.AlwaysStencilFunc,
          stencilFail: THREE.DecrementWrapStencilOp,
          stencilZFail: THREE.DecrementWrapStencilOp,
          stencilZPass: THREE.DecrementWrapStencilOp,
        });
        const frontMesh = new THREE.Mesh(visMesh.geometry, frontMat);
        frontMesh.position.copy(visMesh.position);
        frontMesh.quaternion.copy(visMesh.quaternion);
        frontMesh.renderOrder = 1;
        scene.add(frontMesh);
      }

      const capGeometry = new THREE.PlaneGeometry(
        sceneSize * 10,
        sceneSize * 10,
      );
      const capMaterial = new THREE.MeshBasicMaterial({
        color: 0x9ec5e8,
        side: THREE.DoubleSide,
        stencilWrite: true,
        stencilRef: 0,
        stencilFunc: THREE.NotEqualStencilFunc,
        stencilFail: THREE.ReplaceStencilOp,
        stencilZFail: THREE.ReplaceStencilOp,
        stencilZPass: THREE.ReplaceStencilOp,
      });
      const capMesh = new THREE.Mesh(capGeometry, capMaterial);
      capMesh.renderOrder = 3;

      const [wx, wy] = sectionCenterCoordinate!;
      capMesh.position.set(wx, wy, center.z);
      capMesh.quaternion.setFromUnitVectors(
        new THREE.Vector3(0, 0, 1),
        camDirVec,
      );
      scene.add(capMesh);
    }

    const aspect = w / h;
    const viewSize = sceneSize * 0.8;
    let animFrameId: number | null = null;

    // Force fixed mode for section views (A/B/C/D)
    const effectiveMode: ViewMode = selectedSection === "isometric" ? viewMode : "fixed";

    if (effectiveMode === "walk") {
      // --- Walk / FPS mode ---
      const EYE_HEIGHT = 1.6;
      const camera = new THREE.PerspectiveCamera(75, aspect, 0.01, sceneSize * 20);
      if (walkCameraStateRef.current) {
        camera.position.copy(walkCameraStateRef.current.position);
      } else {
        camera.position.set(center.x, center.y, minZ + EYE_HEIGHT);
      }
      camera.up.set(0, 0, 1);

      const lookDir = walkCameraStateRef.current
        ? walkCameraStateRef.current.lookDir.clone()
        : new THREE.Vector3();

      if (!walkCameraStateRef.current) {
        const lookTarget = new THREE.Vector3(center.x + 1, center.y, minZ + EYE_HEIGHT);
        camera.lookAt(lookTarget);
        camera.getWorldDirection(lookDir);
        lookDir.normalize();
      } else {
        const target = camera.position.clone().add(lookDir);
        camera.lookAt(target);
      }

      const pressed = new Set<string>();
      let shiftDown = false;
      const WALK_KEYS = new Set(["w", "a", "s", "d", "q", "e"]);
      const isEditable = (t: EventTarget | null) => {
        if (!(t instanceof HTMLElement)) return false;
        if (t.isContentEditable) return true;
        const tag = t.tagName;
        return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
      };
      const onKeyDown = (e: KeyboardEvent) => {
        if (isEditable(e.target)) return;
        if (e.key === "Shift") { shiftDown = true; return; }
        const key = e.key.toLowerCase();
        if (!WALK_KEYS.has(key)) return;
        pressed.add(key);
        e.preventDefault();
      };
      const onKeyUp = (e: KeyboardEvent) => {
        if (e.key === "Shift") { shiftDown = false; return; }
        pressed.delete(e.key.toLowerCase());
      };
      const onBlur = () => { pressed.clear(); shiftDown = false; };

      window.addEventListener("keydown", onKeyDown);
      window.addEventListener("keyup", onKeyUp);
      window.addEventListener("blur", onBlur);

      const ROT_SPEED = 0.003;
      const MAX_PITCH = Math.PI / 2 - 0.01;
      const WORLD_UP = new THREE.Vector3(0, 0, 1);
      const yawQ = new THREE.Quaternion();
      const pitchQ = new THREE.Quaternion();
      const rightAxis = new THREE.Vector3();
      let dragging = false;
      let lookDirty = false;

      const DRAG_THRESHOLD = 5;
      const raycaster = new THREE.Raycaster();
      const pointer = new THREE.Vector2();
      // Position saved before pointer lock (clientX/Y freeze once lock is active)
      let selectPos = { x: 0, y: 0 };
      let accMovX = 0;
      let accMovY = 0;

      const onPointerDown = (e: PointerEvent) => {
        if (e.button !== 0) return;
        dragging = false;
        accMovX = 0;
        accMovY = 0;
        selectPos = { x: e.clientX, y: e.clientY };
        renderer.domElement.requestPointerLock?.();
        e.preventDefault();
      };
      const onPointerMove = (e: PointerEvent) => {
        if (!(e.buttons & 1)) return;
        if (!dragging) {
          accMovX += e.movementX;
          accMovY += e.movementY;
          if (Math.sqrt(accMovX * accMovX + accMovY * accMovY) > DRAG_THRESHOLD) {
            dragging = true;
          }
        }
        if (!dragging) return;
        const dx = e.movementX;
        const dy = e.movementY;
        if (dx === 0 && dy === 0) return;

        if (dx !== 0) {
          yawQ.setFromAxisAngle(WORLD_UP, -dx * ROT_SPEED);
          lookDir.applyQuaternion(yawQ).normalize();
        }
        if (dy !== 0) {
          rightAxis.crossVectors(lookDir, WORLD_UP);
          if (rightAxis.lengthSq() < 1e-8) rightAxis.set(1, 0, 0);
          rightAxis.normalize();
          const currentPitch = Math.asin(THREE.MathUtils.clamp(lookDir.z, -1, 1));
          const desiredPitch = THREE.MathUtils.clamp(
            currentPitch + -dy * ROT_SPEED,
            -MAX_PITCH,
            MAX_PITCH,
          );
          const deltaPitch = desiredPitch - currentPitch;
          if (Math.abs(deltaPitch) > 1e-6) {
            pitchQ.setFromAxisAngle(rightAxis, deltaPitch);
            lookDir.applyQuaternion(pitchQ).normalize();
          }
        }
        lookDirty = true;
      };
      const onPointerUp = (e: PointerEvent) => {
        if (e.button !== 0) return;
        document.exitPointerLock?.();
        if (!dragging && onItemClickRef.current) {
          // Use the pre-lock position for raycasting (clientX/Y frozen under pointer lock)
          const rect = renderer.domElement.getBoundingClientRect();
          pointer.x = ((selectPos.x - rect.left) / rect.width) * 2 - 1;
          pointer.y = -((selectPos.y - rect.top) / rect.height) * 2 + 1;
          raycaster.setFromCamera(pointer, camera);
          const hit = raycaster.intersectObjects(visualMeshes, false)[0];
          const item = hit?.object.userData.item as RoomSectionRenderItem["item"] | undefined;
          if (item) onItemClickRef.current(item);
        }
        dragging = false;
      };
      const onPointerLockChange = () => {
        if (document.pointerLockElement !== renderer.domElement) dragging = false;
      };
      const onContextMenu = (e: MouseEvent) => { e.preventDefault(); };

      renderer.domElement.addEventListener("pointerdown", onPointerDown);
      renderer.domElement.addEventListener("pointermove", onPointerMove);
      renderer.domElement.addEventListener("pointerup", onPointerUp);
      renderer.domElement.addEventListener("pointercancel", onPointerUp);
      renderer.domElement.addEventListener("contextmenu", onContextMenu);
      document.addEventListener("pointerlockchange", onPointerLockChange);

      const WALK_SPEED = sceneSize * 0.3;
      const SHIFT_MULT = 3;
      const forwardHoriz = new THREE.Vector3();
      const rightVec = new THREE.Vector3();
      const move = new THREE.Vector3();
      let lastTime = performance.now();

      const tick = () => {
        animFrameId = requestAnimationFrame(tick);
        const now = performance.now();
        const dt = Math.min(0.1, (now - lastTime) / 1000);
        lastTime = now;

        const hasMovement = pressed.size > 0;
        if (!hasMovement && !lookDirty) {
          renderer.render(scene, camera);
          return;
        }

        move.set(0, 0, 0);
        if (hasMovement) {
          forwardHoriz.copy(lookDir);
          forwardHoriz.z = 0;
          if (forwardHoriz.lengthSq() < 1e-8) forwardHoriz.set(1, 0, 0);
          forwardHoriz.normalize();
          rightVec.crossVectors(forwardHoriz, WORLD_UP);
          if (rightVec.lengthSq() < 1e-8) rightVec.set(0, 1, 0);
          rightVec.normalize();

          const speed = WALK_SPEED * (shiftDown ? SHIFT_MULT : 1);
          const step = speed * dt;
          if (pressed.has("w")) move.addScaledVector(forwardHoriz, step);
          if (pressed.has("s")) move.addScaledVector(forwardHoriz, -step);
          if (pressed.has("d")) move.addScaledVector(rightVec, step);
          if (pressed.has("a")) move.addScaledVector(rightVec, -step);
          if (pressed.has("q")) move.z += step;
          if (pressed.has("e")) move.z -= step;
        }

        camera.position.add(move);
        const target = camera.position.clone().add(lookDir);
        camera.lookAt(target);
        lookDirty = false;

        renderer.render(scene, camera);
      };
      animFrameId = requestAnimationFrame(tick);

      const resizeObserver = new ResizeObserver(() => {
        const newW = container.clientWidth;
        const newH = container.clientHeight;
        camera.aspect = newW / newH;
        camera.updateProjectionMatrix();
        renderer.setSize(newW, newH);
      });
      resizeObserver.observe(container);

      return () => {
        if (animFrameId != null) cancelAnimationFrame(animFrameId);
        window.removeEventListener("keydown", onKeyDown);
        window.removeEventListener("keyup", onKeyUp);
        window.removeEventListener("blur", onBlur);
        renderer.domElement.removeEventListener("pointerdown", onPointerDown);
        renderer.domElement.removeEventListener("pointermove", onPointerMove);
        renderer.domElement.removeEventListener("pointerup", onPointerUp);
        renderer.domElement.removeEventListener("pointercancel", onPointerUp);
        renderer.domElement.removeEventListener("contextmenu", onContextMenu);
        document.removeEventListener("pointerlockchange", onPointerLockChange);
        if (document.pointerLockElement === renderer.domElement) document.exitPointerLock?.();
        walkCameraStateRef.current = { position: camera.position.clone(), lookDir: lookDir.clone() };
        resizeObserver.disconnect();
        disposeSceneResources(scene);
        renderer.renderLists.dispose();
        renderer.dispose();
        renderer.forceContextLoss();
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      };
    } else if (effectiveMode === "orbit") {
      // --- Perspective camera + OrbitControls (full 3D rotation) ---
      const camera = new THREE.PerspectiveCamera(60, aspect, 0.1, sceneSize * 20);
      if (orbitCameraStateRef.current) {
        camera.position.copy(orbitCameraStateRef.current.position);
      } else {
        camera.position.copy(center).addScaledVector(camDirVec, sceneSize * 2);
      }
      camera.up.set(0, 0, 1);
      camera.lookAt(orbitCameraStateRef.current?.target ?? center);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.target.copy(orbitCameraStateRef.current?.target ?? center);
      controls.enableDamping = true;
      controls.dampingFactor = 0.15;

      const animate = () => {
        animFrameId = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      // Click-to-select: track pointer-down position to distinguish click from drag
      const raycaster = new THREE.Raycaster();
      const pointer = new THREE.Vector2();
      let pointerDownPos = { x: 0, y: 0 };

      const handlePointerDown = (event: PointerEvent) => {
        pointerDownPos = { x: event.clientX, y: event.clientY };
      };

      const handleClick = (event: MouseEvent) => {
        if (event.button !== 0) return;
        if (!onItemClickRef.current) return;
        const dx = event.clientX - pointerDownPos.x;
        const dy = event.clientY - pointerDownPos.y;
        if (Math.sqrt(dx * dx + dy * dy) > 5) return; // was a drag, not a click

        const rect = renderer.domElement.getBoundingClientRect();
        pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(pointer, camera);
        const hit = raycaster.intersectObjects(visualMeshes, false)[0];
        const item = hit?.object.userData.item as RoomSectionRenderItem["item"] | undefined;
        if (item) onItemClickRef.current(item);
      };

      renderer.domElement.addEventListener("pointerdown", handlePointerDown);
      renderer.domElement.addEventListener("click", handleClick);

      const resizeObserver = new ResizeObserver(() => {
        const newW = container.clientWidth;
        const newH = container.clientHeight;
        camera.aspect = newW / newH;
        camera.updateProjectionMatrix();
        renderer.setSize(newW, newH);
      });
      resizeObserver.observe(container);

      return () => {
        if (animFrameId != null) cancelAnimationFrame(animFrameId);
        orbitCameraStateRef.current = { position: camera.position.clone(), target: controls.target.clone() };
        controls.dispose();
        renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
        renderer.domElement.removeEventListener("click", handleClick);
        resizeObserver.disconnect();
        disposeSceneResources(scene);
        renderer.renderLists.dispose();
        renderer.dispose();
        renderer.forceContextLoss();
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      };
    } else {
      // --- Fixed: Orthographic camera (original behavior) ---
      const camera = new THREE.OrthographicCamera(
        (-viewSize * aspect) / 2,
        (viewSize * aspect) / 2,
        viewSize / 2,
        -viewSize / 2,
        0.1,
        sceneSize * 20,
      );

      camera.position.copy(center).addScaledVector(camDirVec, sceneSize * 5);
      camera.up.set(0, 0, 1);
      camera.lookAt(center);

      const render = () => {
        renderer.render(scene, camera);
      };

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.target.copy(center);
      controls.enableDamping = false;
      controls.enableRotate = false;
      controls.enablePan = true;
      controls.enableZoom = true;
      controls.screenSpacePanning = true;
      controls.zoomSpeed = 1.8;
      controls.panSpeed = 0.9;
      controls.minZoom = 0.2;
      controls.maxZoom = 20;
      controls.mouseButtons = {
        LEFT: null,
        MIDDLE: null,
        RIGHT: null,
      };
      controls.touches = {
        ONE: THREE.TOUCH.PAN,
        TWO: THREE.TOUCH.DOLLY_PAN,
      };
      controls.addEventListener("change", render);
      controls.update();

      render();

      const raycaster = new THREE.Raycaster();
      const pointer = new THREE.Vector2();
      let middlePanStartPosition: { x: number; y: number } | null = null;

      const handlePointerDown = (event: PointerEvent) => {
        if (event.button !== 1) return;

        middlePanStartPosition = { x: event.clientX, y: event.clientY };
        event.preventDefault();
        renderer.domElement.style.cursor = "grabbing";
      };

      const handlePointerMove = (event: PointerEvent) => {
        if (!middlePanStartPosition || event.buttons !== 4) return;

        const rect = renderer.domElement.getBoundingClientRect();
        const dx = event.clientX - middlePanStartPosition.x;
        const dy = event.clientY - middlePanStartPosition.y;
        middlePanStartPosition = { x: event.clientX, y: event.clientY };

        const worldPerPixelX = (camera.right - camera.left) / camera.zoom / rect.width;
        const worldPerPixelY = (camera.top - camera.bottom) / camera.zoom / rect.height;
        const panOffset = new THREE.Vector3();
        const cameraRight = new THREE.Vector3();
        const cameraUp = new THREE.Vector3();

        camera.updateMatrixWorld();
        cameraRight.setFromMatrixColumn(camera.matrix, 0);
        cameraUp.setFromMatrixColumn(camera.matrix, 1);
        panOffset
          .addScaledVector(cameraRight, -dx * worldPerPixelX)
          .addScaledVector(cameraUp, dy * worldPerPixelY);

        camera.position.add(panOffset);
        controls.target.add(panOffset);
        controls.update();
        render();
      };

      const handlePointerUp = () => {
        middlePanStartPosition = null;
        renderer.domElement.style.cursor = "default";
      };

      const handleClick = (event: MouseEvent) => {
        if (event.button !== 0) return;
        if (!onItemClickRef.current) return;

        const rect = renderer.domElement.getBoundingClientRect();
        pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(pointer, camera);

        const hit = raycaster.intersectObjects(visualMeshes, false)[0];
        const item = hit?.object.userData.item as
          | RoomSectionRenderItem["item"]
          | undefined;
        if (item) {
          onItemClickRef.current(item);
        }
      };

      renderer.domElement.style.cursor = "default";
      renderer.domElement.style.touchAction = "none";
      renderer.domElement.addEventListener("pointerdown", handlePointerDown);
      renderer.domElement.addEventListener("pointermove", handlePointerMove);
      renderer.domElement.addEventListener("pointerup", handlePointerUp);
      renderer.domElement.addEventListener("pointerleave", handlePointerUp);
      renderer.domElement.addEventListener("click", handleClick);

      const resizeObserver = new ResizeObserver(() => {
        const newW = container.clientWidth;
        const newH = container.clientHeight;
        const newAspect = newW / newH;
        camera.left = (-viewSize * newAspect) / 2;
        camera.right = (viewSize * newAspect) / 2;
        camera.updateProjectionMatrix();
        renderer.setSize(newW, newH);
        render();
      });
      resizeObserver.observe(container);

      return () => {
        controls.removeEventListener("change", render);
        controls.dispose();
        renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
        renderer.domElement.removeEventListener("pointermove", handlePointerMove);
        renderer.domElement.removeEventListener("pointerup", handlePointerUp);
        renderer.domElement.removeEventListener("pointerleave", handlePointerUp);
        renderer.domElement.removeEventListener("click", handleClick);
        resizeObserver.disconnect();
        disposeSceneResources(scene);
        renderer.renderLists.dispose();
        renderer.dispose();
        renderer.forceContextLoss();
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      };
    }
  }, [
    renderItems,
    cameraDir.x,
    cameraDir.y,
    cameraDir.z,
    sectionCenterCoordinate,
    selectedSection,
    viewMode,
  ]);

  const modeButtonStyle = (active: boolean): React.CSSProperties => ({
    height: 26,
    borderRadius: 4,
    border: "none",
    background: active ? "#228be6" : "transparent",
    color: active ? "#fff" : "#555",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 3,
    fontSize: 11,
    fontWeight: 500,
    fontFamily: "inherit",
    lineHeight: 1,
    padding: "0 7px",
    whiteSpace: "nowrap",
  });

  return (
    <div ref={mountRef} style={{ width: "100%", height: "100%", position: "relative" }}>
      {selectedSection === "isometric" && (
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
            display: "flex",
            gap: 2,
            background: "#fff",
            borderRadius: 6,
            border: "1px solid #ccc",
            padding: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
          }}
        >
          <button
            onClick={() => setViewMode("orbit")}
            title="3D orbit view (rotate/pan/zoom)"
            style={modeButtonStyle(viewMode === "orbit")}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <ellipse cx="12" cy="12" rx="9" ry="4" />
              <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
            </svg>
            3D
          </button>
          <button
            onClick={() => setViewMode("walk")}
            title="Walk mode (WASD + mouse look)"
            style={modeButtonStyle(viewMode === "walk")}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="4" r="2" />
              <path d="M12 6v6l-3 5M12 12l3 5M9 12l-2-1M15 12l2-1" />
            </svg>
            Walk
          </button>
        </div>
      )}

      {/* Walk mode controls hint */}
      {selectedSection === "isometric" && viewMode === "walk" && (
        <div
          style={{
            position: "absolute",
            bottom: 10,
            right: 10,
            zIndex: 10,
            pointerEvents: "none",
            userSelect: "none",
            textAlign: "right",
            fontSize: 11,
            lineHeight: 1.7,
            color: "rgba(255,255,255,0.9)",
            textShadow: "0 1px 3px rgba(0,0,0,0.8)",
            fontFamily: "inherit",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 2, fontSize: 12 }}>Gå-modus</div>
          <div>Klikk + dra — se deg rundt</div>
          <div>Klikk — velg objekt</div>
          <div>W / S — frem / tilbake</div>
          <div>A / D — sideveis</div>
          <div>Q / E — opp / ned</div>
          <div>Shift — raskere</div>
        </div>
      )}
    </div>
  );
};
